import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/kibo-ui/combobox';
import { cn } from '@/lib/utils';
import { ChevronsUpDownIcon, FilmIcon } from 'lucide-react';
import { useState } from 'react';
import videoStylesData from '@/lib/video-styles.json';
import { useI18n } from '@/providers/i18n';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';

type StyleOption = {
  value: string;
  label: string;
  prompt: string;
  description?: string;
};

const STYLE_PRESETS: StyleOption[] = videoStylesData.styles as any;

type VideoStyleSelectorProps = {
  id?: string;
  value: string;
  width?: number | string | 'auto';
  className?: string;
  onChange?: (value: string, prompt: string) => void;
  disabled?: boolean;
};

export const VideoStyleSelector = ({
  id,
  value,
  width = 200,
  className,
  onChange,
  disabled,
}: VideoStyleSelectorProps) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const isPreview = usePreviewCanvas();
  const effectiveDisabled = Boolean(disabled || isPreview);
  const selectedStyle = STYLE_PRESETS.find((s) => s.value === value) || STYLE_PRESETS[0];

  return (
    <Combobox
      open={open}
      onOpenChange={(next) => {
        if (effectiveDisabled) return;
        setOpen(next);
      }}
      data={STYLE_PRESETS.map((style) => ({
        label: style.value === 'none' ? t('No Style') : style.value === 'custom' ? t('Custom') : style.label,
        value: style.value,
      }))}
      type="style"
      value={value}
      onValueChange={(val) => {
        const style = STYLE_PRESETS.find((s) => s.value === val);
        if (style) onChange?.(val, style.prompt);
        setOpen(false);
      }}
    >
      <ComboboxTrigger
        id={id}
        variant="ghost"
        className={cn('rounded-md h-8', className)}
        style={width === 'auto' ? {} : { width }}
        disabled={effectiveDisabled}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FilmIcon size={14} className="text-muted-foreground" />
            <span className="whitespace-nowrap">{selectedStyle.value === 'none' ? t('No Style') : selectedStyle.value === 'custom' ? t('Custom') : selectedStyle.label}</span>
          </div>
          <ChevronsUpDownIcon size={14} className="shrink-0 text-muted-foreground" />
        </div>
      </ComboboxTrigger>
      <ComboboxContent className="p-0" popoverOptions={{ className: 'rounded-md border bg-popover p-0 shadow-md' }}>
        <ComboboxInput className="h-9" />
        <ComboboxList>
          <ComboboxEmpty />
          <ComboboxGroup>
            {STYLE_PRESETS.map((style) => (
              <ComboboxItem
                key={style.value}
                value={style.value}
                onSelect={() => {
                  onChange?.(style.value, style.prompt);
                  setOpen(false);
                }}
                className="gap-2 py-1.5"
              >
                <FilmIcon size={14} className="text-muted-foreground" />
                <div className="flex min-w-0 flex-col">
                  <span className="block truncate leading-5">{style.value === 'none' ? t('No Style') : style.value === 'custom' ? t('Custom') : style.label}</span>
                  {style.description ? (
                    <span className="block truncate text-xs leading-4 text-muted-foreground">
                      {style.description}
                    </span>
                  ) : null}
                </div>
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};


